import {
  createEffect,
  createSignal,
  createUniqueId,
  For,
  Match,
  on,
  Show,
  Switch,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { MorphView } from "@/components/ui/expand-card";
import { cn } from "@/libs/cn";
import { retryTaxonomy } from "../../api";
import type { SectorDraft, SlotIndex } from "../../selector/types";
import {
  categoryLabel,
  categoryOptions,
  subcategoriesOf,
  taxonomyStatus,
} from "../../store/taxonomy-store";
import { AddSecondButton } from "./add-second-button";
import { CategoryGrid } from "./category-grid";
import { CategoryCounter, FOCUS_RING } from "./primitives";
import { ProposalCard } from "./proposal-card";
import { SlotSection } from "./slot-section";
import { type RevealTarget, useRevealSecondary } from "./use-reveal-secondary";

const I18N = "taxonomy.sector" as const;

type Face = "grid" | "subs";

/** What a surface can ask the selector once it is mounted. */
export type SectorSelectorControls = {
  /**
   * The focus on open (AC26): the first subcategory row with a sector, the
   * first tile without; never the trash.
   */
  focusInitial: () => void;
};

export type SectorSelectorProps = {
  draft: SectorDraft;
  /** Writes into the surface's `aria-live="polite"` region (the modal or the step). */
  announce: (text: string) => void;
  /** Height of a fixed bar over the bottom of the scroll container (the onboarding). */
  bottomInset?: number;
  /** Height of a fixed header over the top of the scroll container (the onboarding). */
  topInset?: number;
  controls?: (api: SectorSelectorControls) => void;
  class?: string;
};

/** Usable only if it is in the page and not in the face that is leaving. */
const live = <T extends HTMLElement>(el: T | undefined): T | undefined =>
  el?.isConnected && !el.closest("[data-leaving]") ? el : undefined;

/** The tree is there: the status says so, or a tree was stored without a load (tests). */
const treeReady = () =>
  taxonomyStatus() === "ready" ||
  categoryOptions().some((option) => subcategoriesOf(option.value).length > 0);

/**
 * The sector selector, the «Morph» winner: one face at a time inside the house
 * `MorphView`. The grid of the 8 sectors and the subcategories of the chosen
 * sectors are its two faces. Touching a tile goes straight to that sector's
 * subcategories; the second sector is added from there, back on the grid with
 * the primary lit; once picked, the secondary's section is brought into view.
 *
 * Motion: only the `MorphView` values (M1–M3) and the 150 ms colors of the
 * pieces (M4). The scroll and the focus after a morph go through
 * `useRevealSecondary` (M5). The surface owns the title, the live region and
 * the commands.
 */
export const SectorSelector = (props: SectorSelectorProps) => {
  const { t } = useI18n();
  const uid = createUniqueId();
  const gridTitleId = `sector-${uid}-grid-title`;
  const headingId = (index: SlotIndex) => `sector-${uid}-slot-${index}`;

  const [face, setFace] = createSignal<Face>(
    props.draft.chosenCategories().length > 0 ? "subs" : "grid",
  );
  // The last sector went: the subcategories face has nothing left to show.
  createEffect(() => {
    if (props.draft.chosenCategories().length === 0) setFace("grid");
  });

  const headings: Partial<Record<SlotIndex, HTMLHeadingElement>> = {};
  const sections: Partial<Record<SlotIndex, HTMLElement>> = {};
  let grid: HTMLDivElement | undefined;
  let retryButton: HTMLButtonElement | undefined;

  const { reveal } = useRevealSecondary({
    bottomInset: () => props.bottomInset ?? 0,
    topInset: () => props.topInset ?? 0,
  });

  const firstTile = () =>
    live(grid)?.querySelector<HTMLButtonElement>("button:not(:disabled)") ??
    undefined;

  const sectionTarget =
    (index: SlotIndex, whole: boolean) => (): RevealTarget | null => {
      const focus = live(headings[index]);
      if (!focus) return null;
      return { focus, section: live(sections[index]), whole };
    };

  const tileTarget = (): RevealTarget | null => {
    const focus = firstTile();
    return focus ? { focus } : null;
  };

  const label = (index: SlotIndex) =>
    categoryLabel(props.draft.slots[index]?.category ?? "");

  /** A sector landed in `index`: show its subcategories and bring them to the user. */
  const showSector = (index: SlotIndex) => {
    setFace("subs");
    props.announce(
      index === 0
        ? t(`${I18N}.announce.subcategories`, { label: label(0) })
        : t(`${I18N}.announce.secondaryAdded`, { label: label(1) }),
    );
    // The primary appears where the grid was; the secondary must fit whole (AC6).
    reveal(sectionTarget(index, index === 1));
  };

  const addSecond = () => {
    setFace("grid");
    props.announce(t(`${I18N}.announce.chooseSecond`));
    reveal(tileTarget);
  };

  const continueWithOne = () => {
    setFace("subs");
    props.announce(t(`${I18N}.announce.subcategories`, { label: label(0) }));
    reveal(sectionTarget(0, false));
  };

  const removed = (index: SlotIndex) => {
    if (props.draft.chosenCategories().length === 0) {
      setFace("grid");
      reveal(tileTarget);
      return;
    }
    // The secondary rose to primary, or the secondary went: section 0 remains.
    props.announce(
      index === 0
        ? t(`${I18N}.announce.primaryChanged`, { label: label(0) })
        : t(`${I18N}.announce.secondaryRemoved`),
    );
    reveal(sectionTarget(0, false));
  };

  const swapped = () => {
    // «Rendi principale» stays mounted on the secondary: the focus stays on it.
    props.announce(t(`${I18N}.announce.primaryChanged`, { label: label(0) }));
  };

  const focusInitial = () => {
    const target =
      face() === "subs"
        ? live(sections[0])?.querySelector<HTMLButtonElement>(
            "button[aria-pressed]:not(:disabled)",
          )
        : firstTile();
    target?.focus({ preventScroll: true });
  };
  props.controls?.({ focusInitial });

  // After «Riprova», the focus follows the outcome instead of falling on `body`.
  let retrying = false;
  createEffect(
    on(taxonomyStatus, (status) => {
      if (!retrying || status === "loading") return;
      retrying = false;
      queueMicrotask(() =>
        status === "failed" ? retryButton?.focus() : focusInitial(),
      );
    }),
  );
  const retry = () => {
    retrying = true;
    void retryTaxonomy();
  };

  return (
    <div class={cn("@container w-full", props.class)}>
      <Switch
        fallback={
          <MorphView
            stateKey={face()}
            fullWidth
            render={(key) => (
              <Show
                when={key === "grid"}
                fallback={
                  <div class="flex w-full flex-col gap-5">
                    <For each={[0, 1] as const}>
                      {(index) => (
                        <SlotSection
                          draft={props.draft}
                          index={index}
                          headingId={headingId(index)}
                          headingRef={(el) => {
                            headings[index] = el;
                          }}
                          sectionRef={(el) => {
                            sections[index] = el;
                          }}
                          onRemoved={removed}
                          onSwapped={swapped}
                        />
                      )}
                    </For>
                    <ProposalCard draft={props.draft} onAccepted={showSector} />
                    <Show when={props.draft.chosenCategories().length === 1}>
                      <AddSecondButton onClick={addSecond} />
                    </Show>
                  </div>
                }
              >
                <GridFace
                  draft={props.draft}
                  titleId={gridTitleId}
                  gridRef={(el) => {
                    grid = el;
                  }}
                  onPick={showSector}
                  onContinue={continueWithOne}
                />
              </Show>
            )}
          />
        }
      >
        <Match when={!treeReady() && taxonomyStatus() === "failed"}>
          <div class="flex flex-col items-start gap-3">
            <p role="alert" class="text-sm text-foreground">
              {t(`${I18N}.loadError`)}
            </p>
            <Button
              ref={retryButton}
              variant="outline"
              size="sm"
              class={FOCUS_RING}
              onClick={retry}
            >
              {t(`${I18N}.retry`)}
            </Button>
          </div>
        </Match>
        <Match when={!treeReady()}>
          <TileSkeleton />
        </Match>
      </Switch>
    </div>
  );
};

/**
 * The grid face. Plain, the grid takes its name from a `sr-only` «I settori»
 * and shows only the counter: the surface gives the title (AC8). Adding the
 * second sector, it shows «Aggiungi un secondo settore», «Consigliato: X resta
 * il principale.», `1/2` and «Continua con uno solo» (AC6).
 */
const GridFace = (props: {
  draft: SectorDraft;
  titleId: string;
  gridRef: (el: HTMLDivElement) => void;
  onPick: (index: SlotIndex) => void;
  onContinue: () => void;
}) => {
  const { t } = useI18n();
  const addingSecond = () => props.draft.chosenCategories().length === 1;

  return (
    <div ref={(el) => props.gridRef(el)} class="flex w-full flex-col gap-4">
      <div class="flex items-baseline justify-between gap-2">
        <Show
          when={addingSecond()}
          fallback={
            <span id={props.titleId} class="sr-only">
              {t(`${I18N}.title`)}
            </span>
          }
        >
          <div class="flex min-w-0 flex-col gap-0.5">
            <span id={props.titleId} class="text-sm font-semibold">
              {t(`${I18N}.addSecond`)}
            </span>
            <span class="text-xs text-foreground/70">
              {t(`${I18N}.recommendedHint`, {
                label: categoryLabel(props.draft.slots[0]?.category ?? ""),
              })}
            </span>
          </div>
        </Show>
        <CategoryCounter draft={props.draft} class="ml-auto shrink-0" />
      </div>
      <CategoryGrid
        draft={props.draft}
        labelledBy={props.titleId}
        onPick={props.onPick}
      />
      <Show when={addingSecond()}>
        <div class="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            class={FOCUS_RING}
            onClick={() => props.onContinue()}
          >
            {t(`${I18N}.continueWithOne`)}
            <i class="ph ph-arrow-right ml-1" aria-hidden="true" />
          </Button>
        </div>
      </Show>
    </div>
  );
};

/** Eight tiles while the tree loads (AC15): `animate-pulse` only (M8). */
const TileSkeleton = () => (
  <ul
    class="grid grid-cols-2 gap-3 @md:grid-cols-3 @2xl:grid-cols-4"
    aria-hidden="true"
  >
    <For each={Array.from({ length: 8 })}>
      {() => <li class="min-h-24 animate-pulse rounded-lg bg-muted" />}
    </For>
  </ul>
);
